import { useState, useEffect, useCallback, useRef } from "react";
import { getFundraisingRounds, createFundraisingRound, updateFundraisingRound, deleteFundraisingRound } from "../api";
import { useModel } from "../ModelProvider";
import ViewOnly, { ViewOnlyNote } from "../components/ViewOnly";

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => {
  if (n === null || n === undefined || n === 0) return "--";
  const a = Math.abs(n);
  const s = a >= 1e6 ? "$" + (a / 1e6).toFixed(2) + "M" : a >= 1e3 ? "$" + (a / 1e3).toFixed(1) + "K" : "$" + a.toFixed(2);
  return n < 0 ? "(" + s + ")" : s;
};
const fmtFull = n => {
  if (n === null || n === undefined || n === 0) return "--";
  return "$" + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";
const BLACK = "#1A1C2E";
const BLUE = "#2A6DB0";

// Format number with commas for display in input
// Only shows decimal if user explicitly typed one
const fmtComma = v => {
  let raw = String(v).replace(/[^0-9.]/g, '');
  if (!raw) return '';
  // Strip trailing .00 from DB values
  if (raw.match(/\.\d+$/) && parseFloat(raw) === Math.floor(parseFloat(raw))) {
    raw = String(Math.floor(parseFloat(raw)));
  }
  const hasDecimal = raw.includes('.');
  const parts = raw.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (hasDecimal) return parts[0] + '.' + (parts[1] || '');
  return parts[0];
};
// Strip commas for storage
const stripComma = v => String(v).replace(/,/g, '');

/* MLS AND MNAMES DELETED — MLS had ZERO call sites in this file and was never exported,
   and MNAMES had exactly one reader: MLS. It was the only UNGUARDED month-label copy on
   this page, so the fix here is a delete, not a guard. Removing MLS alone would have left
   MNAMES unused and produced a new lint warning. */

export default function FundraisingPage({ companyId = 1 }) {
  const { markStale, canEditModel } = useModel();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("equity");
  const [saving, setSaving] = useState(false);
  const [draftError, setDraftError] = useState({});   // W-087 F1: a refused POST of a draft, shown beside its row
  const draftSeq = useRef(0);
  // A stored round is keyed by its id; a draft (never stored) by its local _key.
  const rowKey = r => (r.id != null ? r.id : r._key);

  const loadRounds = useCallback(async () => {
    try {
      const res = await getFundraisingRounds(companyId);
      setRounds(res.data || []);
    } catch (err) {
      console.error("Failed to load fundraising rounds:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadRounds(); }, [loadRounds]);

  /* W-087 F1 (Brian 2026-10-02): a NEW round is a LOCAL DRAFT -- "New round", no close date, and NO request. It does not
   * count in the model until its close month is chosen: fundraising_rounds.close_date is NOT NULL (a dateless round cannot
   * be stored), and the model reads only STORED rounds (ModelProvider's own fetch), so a draft cannot reach it. Choosing a
   * month POSTs the round (commitDraft). A reload before that discards the draft (accepted).
   * Was: an immediate POST of { "Series A", close_date "2026-06" } -- a hard-coded month, already closed for a 2026 client. */
  const addRound = () => {
    draftSeq.current += 1;
    const key = "draft-" + draftSeq.current;
    setRounds(p => [{ _draft: true, _key: key, round_name: "New round", round_type: "equity", close_date: "", gross_proceeds: 0, issuance_costs: 0 }, ...p]);
  };

  // A draft's close month was chosen: POST it with what was typed so far; on success the stored row replaces the draft
  // (and the normal PUT-on-change applies from then on). A refusal is shown beside the row and the draft is kept.
  const commitDraft = async (draft, closeDate) => {
    const key = draft._key;
    setDraftError(p => { const n = { ...p }; delete n[key]; return n; });
    try {
      setSaving(true);
      const res = await createFundraisingRound(companyId, {
        round_name: draft.round_name,
        round_type: draft.round_type,
        close_date: closeDate,
        gross_proceeds: draft.gross_proceeds,
        issuance_costs: draft.issuance_costs,
      });
      setRounds(p => p.map(r => (r._key === key ? res.data : r)));
      markStale();
    } catch (err) {
      setRounds(p => p.map(r => (r._key === key ? { ...r, close_date: "" } : r)));
      setDraftError(p => ({ ...p, [key]: String(err?.response?.data?.error || err?.message || "Save failed.") }));
    } finally {
      setSaving(false);
    }
  };

  const saveRound = async (round) => {
    try {
      setSaving(true);
      await updateFundraisingRound(companyId, round.id, {
        round_name: round.round_name,
        round_type: round.round_type,
        close_date: round.close_date,
        gross_proceeds: round.gross_proceeds,
        issuance_costs: round.issuance_costs,
      });
      markStale();
    } catch (err) {
      console.error("Failed to update round:", err);
    } finally {
      setSaving(false);
    }
  };

  const removeRound = async (round) => {
    if (round._draft) {   // W-087 F1: a draft was never stored -- drop it locally, no request
      setRounds(p => p.filter(r => r._key !== round._key));
      setDraftError(p => { const n = { ...p }; delete n[round._key]; return n; });
      return;
    }
    const id = round.id;
    if (!window.confirm("Delete this round? This will remove it from BS and CFS.")) return;
    try {
      setSaving(true);
      await deleteFundraisingRound(companyId, id);
      setRounds(p => p.filter(r => r.id !== id));
      markStale();
    } catch (err) {
      console.error("Failed to delete round:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, field, value) => {
    setRounds(p => p.map(r => rowKey(r) === key ? { ...r, [field]: value } : r));
  };

  const handleBlur = (round) => {
    if (round._draft) return;   // W-087 F1: a draft has no id to PUT; it is POSTed when its close month is chosen
    saveRound(round);
  };

  const equityRounds = rounds.filter(r => r.round_type === "equity");
  // W-087 F1: the summary counts STORED rounds only -- the rounds the model sees. A draft does not count until dated.
  const storedEquity = equityRounds.filter(r => !r._draft);

  const totalGross = storedEquity.reduce((s, r) => r2(s + (parseFloat(r.gross_proceeds) || 0)), 0);
  const totalCosts = storedEquity.reduce((s, r) => r2(s + (parseFloat(r.issuance_costs) || 0)), 0);
  const totalNet = r2(totalGross - totalCosts);

  const tabStyle = (active) => ({
    padding: "8px 20px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: active ? "default" : "pointer",
    border: active ? "1px solid #3350D4" : "1px solid #E2E5F0",
    background: active ? "linear-gradient(180deg,#5A73F5 0%,#3D5AF1 100%)" : "#fff",
    color: active ? "#fff" : "#4B5563",
    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.15),inset 0 1px 0 rgba(255,255,255,0.25)" : "none",
    fontFamily: FONT,
  });

  const iS = {
    padding: "6px 10px",
    border: "1px solid #E2E5F0",
    borderRadius: 6,
    fontSize: 13,
    fontFamily: FONT,
    color: BLACK,
    outline: "none",
  };

  const iSMono = {
    ...iS,
    fontFamily: MONO,
    textAlign: "right",
  };

  /* DELIBERATELY STILL `loading` ALONE. The sibling gates on DAModule and VendorDashboard
     gained `|| !allMonths.length` in this same build; this page does NOT, because it does
     not consume the month window at all (measured: 0 dereferences of allMonths /
     forecastMonths / cutoff on its render path). Adding the check would change WHEN the
     page renders with no defect behind it — a gate added for symmetry is a behaviour
     change, not a fix. */
  if (loading) return <div style={{ padding: 32, fontFamily: FONT, color: "#999" }}>Loading...</div>;

  return (
    <div style={{ padding: "24px 32px", fontFamily: FONT, color: BLACK, maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Fundraising</h1>
          <div style={{ fontSize: 12, color: "#8B8FA3", marginTop: 4 }}>Funding rounds flow to equity on the balance sheet and to financing on the cash flow.</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button style={tabStyle(activeTab === "equity")} onClick={() => setActiveTab("equity")}>Equity</button>
        <button style={tabStyle(activeTab === "debt")} onClick={() => setActiveTab("debt")}>Debt</button>
      </div>

      {activeTab === "debt" && (
        <div style={{ padding: "60px 32px", textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
          Debt fundraising module coming soon.
        </div>
      )}

      {activeTab === "equity" && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
            <div style={{ background: "#fff", border: "1px solid #E2E5F0", borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: 1 }}>Rounds</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{storedEquity.length}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2E5F0", borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: 1 }}>Gross Proceeds</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, fontFamily: MONO, color: "#059669" }}>{fmtFull(totalGross)}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2E5F0", borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: 1 }}>Issuance Costs</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, fontFamily: MONO, color: "#DC2626" }}>{totalCosts > 0 ? "(" + fmtFull(totalCosts) + ")" : "--"}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2E5F0", borderRadius: 10, padding: "16px 20px" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: 1 }}>Net Proceeds</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, fontFamily: MONO, color: BLUE }}>{fmtFull(totalNet)}</div>
            </div>
          </div>

          {/* Add Round Button */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <ViewOnly canEdit={canEditModel}><button
              onClick={addRound}
              disabled={saving}
              style={{
                padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: "1px solid #3350D4", background: "linear-gradient(180deg,#5A73F5 0%,#3D5AF1 100%)",
                color: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.15),inset 0 1px 0 rgba(255,255,255,0.25)",
                fontFamily: FONT, opacity: saving ? 0.6 : 1,
              }}
            >
              + Add Equity Round
            </button></ViewOnly>
          </div>

          {/* Rounds Table */}
          {equityRounds.length === 0 ? (
            <div style={{ padding: "60px 32px", textAlign: "center", color: "#D1D5DB", fontSize: 14, background: "#fff", borderRadius: 10, border: "1px solid #E2E5F0" }}>
              No equity rounds added.{canEditModel ? ' Click "+ Add Equity Round" to model a fundraise.' : <> <ViewOnlyNote/></>}
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #E2E5F0", overflow: "hidden" }}>
              <ViewOnly canEdit={canEditModel}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #E2E5F0", background: "#FAFBFC" }}>
                    <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#6B7280", fontSize: 11 }}>Round Name</th>
                    <th style={{ padding: "10px 16px", textAlign: "center", fontWeight: 600, color: "#6B7280", fontSize: 11 }}>Close Date</th>
                    <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#6B7280", fontSize: 11 }}>Gross Proceeds</th>
                    <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#6B7280", fontSize: 11 }}>Est. Issuance Costs</th>
                    <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#6B7280", fontSize: 11 }}>Net Proceeds</th>
                    <th style={{ padding: "10px 16px", textAlign: "center", fontWeight: 600, color: "#6B7280", fontSize: 11, width: 80 }}>BS Lines</th>
                    <th style={{ padding: "10px 16px", textAlign: "center", fontWeight: 600, color: "#6B7280", fontSize: 11, width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {equityRounds.map((round, idx) => {
                    const gross = parseFloat(round.gross_proceeds) || 0;
                    const costs = parseFloat(round.issuance_costs) || 0;
                    const net = r2(gross - costs);
                    return (
                      <tr key={rowKey(round)} style={{ borderBottom: "1px solid #F5F6FA", background: idx % 2 === 0 ? "#fff" : "#FAFBFC" }}>
                        <td style={{ padding: "10px 16px" }}>
                          <input
                            value={round.round_name}
                            onChange={e => updateField(rowKey(round), "round_name", e.target.value)}
                            onBlur={() => handleBlur(round)}
                            style={{ ...iS, width: 180, fontWeight: 600 }}
                          />
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "center" }}>
                          <input
                            type="month"
                            value={round.close_date}
                            onChange={e => {
                              const pts = e.target.value.split("-");
                              const val = pts[0] + "-" + pts[1];
                              if (round._draft) {   // W-087 F1: the close month makes a draft a round -- POST it now
                                if (!e.target.value) return;
                                updateField(rowKey(round), "close_date", val);
                                commitDraft(round, val);
                                return;
                              }
                              updateField(rowKey(round), "close_date", val);
                              setTimeout(() => saveRound({ ...round, close_date: val }), 0);
                            }}
                            style={{ ...iS, width: 150, fontFamily: MONO, textAlign: "center" }}
                          />
                          {round._draft && !round.close_date && (
                            <div data-fr-mark="required" style={{ fontSize: 11, color: "#B45309", fontWeight: 600, marginTop: 4 }}>Required &mdash; choose a close month</div>
                          )}
                          {round._draft && draftError[round._key] && (
                            <div data-fr-error="draft" style={{ fontSize: 11, color: "#E07A5F", fontWeight: 600, marginTop: 4 }}>{draftError[round._key]}</div>
                          )}
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right" }}>
                          <input
                            value={fmtComma(round.gross_proceeds)}
                            onChange={e => updateField(rowKey(round), "gross_proceeds", stripComma(e.target.value))}
                            onBlur={() => handleBlur(round)}
                            style={{ ...iSMono, width: 150 }}
                            placeholder="0.00"
                          />
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right" }}>
                          <input
                            value={fmtComma(round.issuance_costs)}
                            onChange={e => updateField(rowKey(round), "issuance_costs", stripComma(e.target.value))}
                            onBlur={() => handleBlur(round)}
                            style={{ ...iSMono, width: 150 }}
                            placeholder="0.00"
                          />
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right", fontFamily: MONO, fontWeight: 700, color: BLUE }}>
                          {fmtFull(net)}
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: "#6B7280", lineHeight: 1.4 }}>
                            <div style={{ fontWeight: 600, color: "#059669" }}>{round.round_name}</div>
                            {costs > 0 && <div style={{ color: "#DC2626" }}>{round.round_name} Issuance Costs</div>}
                          </div>
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "center" }}>
                          <button
                            onClick={() => removeRound(round)}
                            style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 16, padding: 4 }}
                            title="Delete round"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></ViewOnly>
            </div>
          )}

          {/* Accounting Flow Info */}
          {equityRounds.length > 0 && (
            <div style={{ marginTop: 24, padding: "16px 20px", background: "#F0F7FF", borderRadius: 8, border: "1px solid #DBEAFE", fontSize: 12, color: "#4B5563", lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: BLACK, marginBottom: 6, fontSize: 13 }}>How this flows through the model:</div>
              <div><span style={{ fontWeight: 600 }}>Balance Sheet</span> — Each round creates a new equity line (gross proceeds) and, if applicable, a separate issuance costs line (contra-equity). Both appear in the Equity section from the close month forward.</div>
              <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600 }}>Cash Flow Statement</span> — Gross proceeds and issuance costs appear as separate Financing Activities lines in the close month. Net cash inflow = gross proceeds − issuance costs.</div>
              <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600 }}>Sweep</span> — Additional cash flows to the money market account via the sweep mechanism, increasing interest income automatically.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
